import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { BehaviorSubject, map, Observable } from 'rxjs';

import { CommonSessionService } from '~ng-common-lib/session/session.service';

import { parseWorkspacesFromGetAllResponse } from './utils/workspace-nodes.utils';
import { ClientWorkspace } from './workspace.type';
import { WorkspacesApiService } from './workspaces.api.service';

@Injectable({ providedIn: 'root' })
export class CommonWorkspacesService {
	private readonly commonSessionService = inject(CommonSessionService);
	private readonly workspacesApi = inject(WorkspacesApiService);

	private readonly loading$$ = new BehaviorSubject(false);
	public readonly loading$ = this.loading$$.asObservable();

	private readonly workspaces$$ = new BehaviorSubject<ClientWorkspace[]>([]);
	public readonly workspaces$ = this.workspaces$$.asObservable();
	public get workspaces(): ClientWorkspace[] {
		return this.workspaces$$.value;
	}

	public readonly $workspaces = signal<ClientWorkspace[]>([]);

	private reqId = 0;

	constructor() {
		effect(() => {
			const userId = this.commonSessionService.$sessionId();

			untracked(() => {
				if (!userId) {
					this.reset();
				}
			});
		});
	}

	public getById$(id: string | null): Observable<ClientWorkspace | null> {
		return this.workspaces$.pipe(
			map((workspaces) => {
				return workspaces.find((workspace) => workspace.id === id) ?? null;
			})
		);
	}

	public async loadWorkspaces(force = false): Promise<void> {
		if (!force && this.workspaces.length > 0) {
			return;
		}

		const reqId = ++this.reqId;

		try {
			this.loading$$.next(true);
			const response = await this.workspacesApi.getAll();
			if (reqId !== this.reqId) {
				return;
			}
			this.setWorkspaces(parseWorkspacesFromGetAllResponse(response));
		} catch (error) {
			console.error(error);
		} finally {
			if (reqId === this.reqId) {
				this.loading$$.next(false);
			}
		}
	}

	public setWorkspaces(workspaces: ClientWorkspace[]): void {
		this.workspaces$$.next(workspaces);
		this.$workspaces.set(workspaces);
	}

	public reset(): void {
		this.reqId++;
		this.loading$$.next(false);
		this.workspaces$$.next([]);
		this.$workspaces.set([]);
	}
}
